import Axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { TrendsResponse } from './pro-api.types';

class BaseAPI {
  private static http: AxiosInstance;
  private static instance: BaseAPI;

  private constructor() {
    BaseAPI.http = this.createAxiosInstance();

    BaseAPI.http.defaults.baseURL =
      process.env.NEXT_PUBLIC_PRO_API_HOST || 'https://pro-api.invideo.io';

    this.interceptRequest(BaseAPI.http);
    this.interceptResponse(BaseAPI.http);
  }

  private createAxiosInstance(): AxiosInstance {
    return Axios.create();
  }

  private interceptRequest(apiInstance: AxiosInstance) {
    apiInstance.interceptors.request.use(
      config => {
        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );
  }

  private interceptResponse(apiInstance: AxiosInstance) {
    apiInstance.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        return Promise.reject(error);
      },
    );
  }

  public static getHTTPInstance(): AxiosInstance {
    if (!this.instance) {
      this.instance = new BaseAPI();
    }
    return BaseAPI.http;
  }
}

const server: AxiosInstance = BaseAPI.getHTTPInstance();

const ProAPI = {
  withMetadata: (metadata: any) => {
    return {
      post: (url: string, data?: any, config?: AxiosRequestConfig) => {
        return server.post(url, { ...data, ...metadata }, config);
      },
      put: (url: string, data?: any, config?: AxiosRequestConfig) => {
        return server.put(url, { ...data, ...metadata }, config);
      },
    };
  },
  get: <T = any>(url: string, config?: AxiosRequestConfig) => {
    return server.get<T>(url, config);
  },
  post: (url: string, data: any, config?: AxiosRequestConfig) => {
    return server.post(url, data, config);
  },
  put: (url: string, data: any, config?: AxiosRequestConfig) => {
    return server.put(url, data, config);
  },
  delete: (url: string, config?: AxiosRequestConfig) => {
    return server.delete(url, config);
  },
};

const getTrends = async () => {
  const response = await ProAPI.get<TrendsResponse>('/api/ai-videos/trends');

  return response.data;
};

/**
 * How to read the credit numbers in the plans catalogs.
 *
 * The `/detailed` endpoints are byte-frozen, so their `usage_quota.credits_v4`
 * values stay in the unit the API stores credits in and anything rendering
 * them to a user multiplies by this. `/api/plans/meta` is unauthenticated on
 * purpose — these pages have no session — and is itself cached for 60s, so the
 * memo below matches its `cache-control` rather than outliving it.
 *
 * Never rejects: a pricing page that cannot reach the endpoint still renders,
 * at the unscaled denomination. A failure is deliberately not memoized, so the
 * next render retries instead of quoting the wrong numbers for a full TTL.
 */
const SCALE_TTL_MS = 60_000;

let cachedScale: { value: number; fetchedAt: number } | null = null;
let inFlightScale: Promise<number> | null = null;

const getCreditsDisplayScale = (): Promise<number> => {
  if (cachedScale && Date.now() - cachedScale.fetchedAt < SCALE_TTL_MS) {
    return Promise.resolve(cachedScale.value);
  }

  if (inFlightScale) return inFlightScale;

  inFlightScale = ProAPI.get<{ credits_display_scale?: number }>(
    '/api/plans/meta',
  )
    .then(response => {
      const raw = response.data?.credits_display_scale;
      const scale =
        typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : 1;

      cachedScale = { value: scale, fetchedAt: Date.now() };
      return scale;
    })
    .catch(() => 1)
    .finally(() => {
      inFlightScale = null;
    });

  return inFlightScale;
};

export { ProAPI, getTrends, getCreditsDisplayScale };
